// SPDX-License-Identifier: AGPL-3.0-or-later

import * as AccessibilityCommands from '@app/features/accessibility/commands/AccessibilityCommands';
import Accessibility from '@app/features/accessibility/state/Accessibility';
import {SettingsTabSection} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {Message} from '@app/features/channel/components/Message';
import {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import {SHIFT_KEY_SYMBOL} from '@app/features/input/utils/KeyboardUtils';
import {Message as MessageModel} from '@app/features/messaging/models/Message';
import {KeyboardKey} from '@app/features/ui/components/KeyboardKey';
import {SwitchGroup, SwitchGroupItem} from '@app/features/ui/components/SwitchGroup';
import MobileLayout from '@app/features/ui/state/MobileLayout';
import styles from '@app/features/user/components/modals/tabs/chat_settings_tab/InteractionTab.module.css';
import Users from '@app/features/user/state/Users';
import {MessageStates, MessageTypes} from '@app/vendor/constants/ChannelConstants';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useEffect, useMemo} from 'react';

const MessageActionBarPreview = observer(
	({
		showActionBar,
		showShiftExpand,
		onlyMoreButton,
	}: {
		showActionBar: boolean;
		showQuickReactions: boolean;
		showShiftExpand: boolean;
		onlyMoreButton: boolean;
	}) => {
		const {t} = useLingui();
		const fakeData = useMemo(() => {
			const currentUser = Users.getCurrentUser();
			const author = currentUser?.toJSON() || {
				id: '1000000000000000010',
				username: 'PreviewUser',
				discriminator: '0000',
				global_name: 'Preview User',
				avatar: null,
				avatar_color: null,
				bot: false,
				system: false,
				flags: 0,
			};
			const fakeChannel = new Channel({
				id: '1000000000000000020',
				type: 0,
				name: 'action-bar-fake-channel',
				position: 0,
				parent_id: null,
				topic: null,
				url: null,
				nsfw: false,
				last_message_id: null,
				last_pin_timestamp: null,
				bitrate: null,
				user_limit: null,
				permission_overwrites: [],
			});
			const fakeMessage = new MessageModel(
				{
					id: '1000000000000000030',
					channel_id: '1000000000000000020',
					author,
					type: MessageTypes.DEFAULT,
					flags: 0,
					pinned: false,
					mention_everyone: false,
					content: showActionBar ? t`This message shows the action bar` : t`This message doesn't show the action bar`,
					timestamp: new Date().toISOString(),
					state: MessageStates.SENT,
				},
				{skipUserCache: true},
			);
			return {fakeChannel, fakeMessage};
		}, [showActionBar]);
		useEffect(() => {
			Channels.handleChannelCreate({channel: fakeData.fakeChannel.toJSON()});
			return () => {
				Channels.handleChannelDelete({channel: fakeData.fakeChannel.toJSON()});
			};
		}, [fakeData.fakeChannel]);
		return (
			<div className={styles.previewContainer}>
				<div
					className={styles.previewBox}
					data-force-show-action-bar={showActionBar ? 'true' : 'false'}
					style={{
						pointerEvents: 'none',
					}}
				>
					{showActionBar && (
						<style>
							{`
							[data-force-show-action-bar="true"] [class*="buttons"] {
								opacity: 1 !important;
								pointer-events: none !important;
							}
							[data-force-show-action-bar="true"] [class*="hoverAction"] {
								opacity: 1 !important;
								pointer-events: none !important;
							}
						`}
						</style>
					)}
					<Message
						channel={fakeData.fakeChannel}
						message={fakeData.fakeMessage}
						removeTopSpacing={true}
						previewMode={true}
					/>
				</div>
				<div
					className={`${styles.shiftHint} ${!showActionBar || onlyMoreButton || !showShiftExpand ? styles.shiftHintDisabled : ''}`}
				>
					<span className={styles.shiftHintText}>{t`Hold`}</span>
					<KeyboardKey>{SHIFT_KEY_SYMBOL}</KeyboardKey>
					<span className={styles.shiftHintText}>{t`to expand action bar`}</span>
				</div>
			</div>
		);
	},
);
export const InteractionTabContent: React.FC = observer(() => {
	const {t} = useLingui();
	const mobileLayout = MobileLayout;
	const {
		showMessageActionBar,
		showMessageActionBarQuickReactions,
		showMessageActionBarShiftExpand,
		showMessageActionBarOnlyMoreButton,
	} = Accessibility;
	if (mobileLayout.enabled) {
		return (
			<SettingsTabSection
				title={t`Message Action Bar`}
				description={t`Message action bar settings are only available on desktop.`}
			>
				{null}
			</SettingsTabSection>
		);
	}
	return (
		<SettingsTabSection
			title={t`Message Action Bar`}
			description={t`Customize the action bar that appears when hovering over messages.`}
		>
			<div className={styles.sectionContent}>
				<MessageActionBarPreview
					showActionBar={showMessageActionBar}
					showQuickReactions={showMessageActionBarQuickReactions}
					showShiftExpand={showMessageActionBarShiftExpand}
					onlyMoreButton={showMessageActionBarOnlyMoreButton}
				/>
				<SwitchGroup>
					<SwitchGroupItem
						label={t`Show message action bar`}
						value={showMessageActionBar}
						onChange={(value) => AccessibilityCommands.update({showMessageActionBar: value})}
					/>
					<SwitchGroupItem
						label={t`Show only more button`}
						value={showMessageActionBarOnlyMoreButton}
						onChange={(value) => AccessibilityCommands.update({showMessageActionBarOnlyMoreButton: value})}
						disabled={!showMessageActionBar}
					/>
					<SwitchGroupItem
						label={t`Show Quick Reactions`}
						value={showMessageActionBarQuickReactions}
						onChange={(value) => AccessibilityCommands.update({showMessageActionBarQuickReactions: value})}
						disabled={!showMessageActionBar || showMessageActionBarOnlyMoreButton}
					/>
					<SwitchGroupItem
						label={t`Enable Shift to Expand`}
						value={showMessageActionBarShiftExpand}
						onChange={(value) => AccessibilityCommands.update({showMessageActionBarShiftExpand: value})}
						disabled={!showMessageActionBar || showMessageActionBarOnlyMoreButton}
					/>
				</SwitchGroup>
			</div>
		</SettingsTabSection>
	);
});
